import React, { useState, useEffect } from 'react';
import { Plus, DoorOpen, AlertCircle, Calendar, Lock, Pencil, Trash2, QrCode, ScanLine } from 'lucide-react';
import QRCode from 'qrcode';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import {
  visitors as api,
  units as unitsApi,
  residents as residentsApi,
  type Visitor, type Unit, type Resident, type ApiError,
} from '../../services/api';
import DataTable from '../../components/DataTable';
import BulkImportExport from '../../components/BulkImportExport';
import StatusFilterBar from '../../components/StatusFilterBar';
import SearchSelect from '../../components/SearchSelect';
import { useServerTable } from '../../hooks/useServerTable';
import { useStatusCounts } from '../../hooks/useStatusCounts';
import { downloadBlob } from '../../utils/downloadBlob';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const INIT = { name: '', phone: '', purpose: '', vehicleNumber: '', appointmentTime: '', recurring: false, recurrenceEndDate: '', unitId: '', residentId: '' };

const MAX_PHOTO_BYTES = 700_000;

function readPhotoAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STATUS_FILTERS = ['all', 'pending', 'approved', 'denied', 'exited'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];

// Natural sort (block, then unit) so "A2" sorts before "A10" rather than after.
const sortUnits = (list: Unit[]) =>
  [...list].sort((a, b) =>
    (a.blockName || '').localeCompare(b.blockName || '', undefined, { numeric: true, sensitivity: 'base' })
    || (a.unitNumber || '').localeCompare(b.unitNumber || '', undefined, { numeric: true, sensitivity: 'base' }));

const isPastAppointment = (v: Visitor) =>
  !!v.appointmentTime && new Date() >= new Date(v.appointmentTime);

// UTC ISO string → datetime-local input value (local wall-clock digits)
const toDatetimeLocal = (utc: string) => {
  if (!utc) return '';
  const d = new Date(utc);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

const statusColor = (s: string) =>
  s === 'approved' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
    : s === 'pending' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
      : s === 'denied' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
        : s === 'PRE_APPROVED' ? { bg: 'rgba(99,102,241,0.1)', text: '#6366F1' }
          : { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };

const QrPreview: React.FC<{ value: string }> = ({ value }) => {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    QRCode.toDataURL(value, { width: 180, margin: 1 }).then(setDataUrl).catch(() => {});
  }, [value]);
  if (!dataUrl) return null;
  return <img src={dataUrl} alt="Visitor pass QR code" style={{ display: 'block', margin: '0 auto', borderRadius: 8 }} />;
};

const VisitorsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isGateStaff = user?.role === 'SOCIETY_ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'STAFF';
  // Browsing the full unit directory is admin/staff-only on the backend — residents/tenants
  // logging a visitor only ever need their own unit, derived from their own resident record.
  const canBrowseUnits = user?.role !== 'RESIDENT' && user?.role !== 'TENANT';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [unitOptions, setUnitOptions] = useState<Unit[]>([]);
  const [residentOptions, setResidentOptions] = useState<Resident[]>([]);

  useEffect(() => {
    if (!societyId) return;
    if (canBrowseUnits) {
      unitsApi.list(societyId, { size: 500 }).then(page => setUnitOptions(sortUnits(page.content))).catch(() => {});
    }
    residentsApi.list(societyId, { size: 500 }).then(page => setResidentOptions(page.content)).catch(() => {});
  }, [societyId, canBrowseUnits]);

  // Residents/tenants can't browse the unit directory, but they only ever log a visitor
  // for their own unit anyway — synthesize that one option from their own resident record.
  useEffect(() => {
    if (canBrowseUnits) return;
    const own = residentOptions[0];
    if (!own) return;
    setUnitOptions([{
      id: own.unitId, societyId: own.societyId, unitNumber: own.unitNumber ?? '',
      blockName: own.blockName, status: 'occupied', createdAt: '',
    }]);
  }, [canBrowseUnits, residentOptions]);

  const unitResidentOptions = (unitId: string) =>
    unitId ? residentOptions.filter(r => r.unitId === unitId) : residentOptions;

  const {
    rows, totalElements, totalPages, loading,
    page, setPage, pageSize, setPageSize,
    sortField, sortDir, setSort, sort,
    q, setQ, refetch,
  } = useServerTable<Visitor>(
    params => societyId
      ? api.list(societyId, { ...params, status: statusFilter === 'all' ? undefined : statusFilter })
      : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId, statusFilter],
  );

  const { counts: statusCounts, loading: statusCountsLoading, refetch: refetchStatusCounts } = useStatusCounts(
    STATUS_FILTERS,
    key => societyId
      ? api.list(societyId, { page: 0, size: 1, status: key === 'all' ? undefined : key }).then(r => r.totalElements)
      : Promise.resolve(0),
    [societyId],
  );

  // Pre-approve (QR + OTP) form
  const [showPreApprove, setShowPreApprove] = useState(false);
  const [preApproveForm, setPreApproveForm] = useState(INIT);
  const [preApproveSaving, setPreApproveSaving] = useState(false);
  const [preApproveError, setPreApproveError] = useState('');
  const [preApproveResult, setPreApproveResult] = useState<Visitor | null>(null);

  // Gate check-in (QR token / OTP)
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInSaving, setCheckInSaving] = useState(false);
  const [checkInError, setCheckInError] = useState('');

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [photoBase64, setPhotoBase64] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [preApprovePhotoBase64, setPreApprovePhotoBase64] = useState('');
  const [preApprovePhotoPreview, setPreApprovePhotoPreview] = useState('');

  // Edit form
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const change = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleExport = async () => {
    if (!societyId) return;
    setExporting(true);
    try {
      const blob = await api.exportCsv(societyId, sort);
      downloadBlob(blob, 'visitors-export.csv');
    } catch { /* ignore */ }
    finally { setExporting(false); }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>, target: 'create' | 'preApprove') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      const msg = 'Photo is too large — please use an image under 500KB.';
      target === 'create' ? setFormError(msg) : setPreApproveError(msg);
      return;
    }
    const b64 = await readPhotoAsBase64(file);
    const preview = `data:${file.type};base64,${b64}`;
    if (target === 'create') { setPhotoBase64(b64); setPhotoPreview(preview); }
    else { setPreApprovePhotoBase64(b64); setPreApprovePhotoPreview(preview); }
  };

  const editChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        name: form.name, phone: form.phone || undefined,
        purpose: form.purpose || undefined, vehicleNumber: form.vehicleNumber || undefined,
        appointmentTime: form.appointmentTime ? new Date(form.appointmentTime).toISOString() : undefined,
        photoBase64: photoBase64 || undefined,
        unitId: form.unitId || undefined, residentId: form.residentId || undefined,
      });
      refetch();
      refetchStatusCounts();
      setShowForm(false); setForm(INIT); setPhotoBase64(''); setPhotoPreview('');
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to log visitor');
    } finally { setSaving(false); }
  };

  const openEdit = (v: Visitor) => {
    setEditId(v.id);
    setEditForm({
      name: v.name,
      phone: v.phone || '',
      vehicleNumber: v.vehicleNumber || '',
      purpose: v.purpose || '',
      appointmentTime: toDatetimeLocal(v.appointmentTime ?? ''),
      recurring: v.recurring ?? false,
      recurrenceEndDate: v.recurrenceEndDate ?? '',
      unitId: v.unitId || '',
      residentId: v.residentId || '',
    });
    setEditError('');
    setShowEdit(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editId) return;
    setEditSaving(true); setEditError('');
    try {
      await api.update(societyId, editId, {
        name: editForm.name, phone: editForm.phone || undefined,
        purpose: editForm.purpose || undefined, vehicleNumber: editForm.vehicleNumber || undefined,
        appointmentTime: editForm.appointmentTime ? new Date(editForm.appointmentTime).toISOString() : undefined,
        unitId: editForm.unitId || undefined, residentId: editForm.residentId || undefined,
      });
      refetch();
      setShowEdit(false); setEditId(null);
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update visitor');
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!societyId) return;
    setDeleteLoading(true);
    try {
      await api.remove(societyId, id);
      refetch();
      refetchStatusCounts();
      setDeleteConfirm(null);
    } catch (err) {
      setActionError((err as ApiError).message || 'Failed to delete visitor');
      setDeleteConfirm(null);
    } finally { setDeleteLoading(false); }
  };

  const doAction = async (id: string, action: 'approve' | 'deny' | 'exit') => {
    if (!societyId) return;
    setActionLoading(id + action);
    setActionError(null);
    try {
      await api[action](societyId, id);
      refetch();
      refetchStatusCounts();
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 403) {
        setActionError('Appointment time has passed, status is locked.');
      } else if (e.status === 409) {
        refetch();
        refetchStatusCounts();
        setActionError(e.message || 'This visitor\'s status was already updated. The list has been refreshed.');
      } else {
        setActionError(e.message || 'Action failed. Please try again.');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const fmt = (iso?: string) =>
    iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const handlePreApprove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setPreApproveSaving(true); setPreApproveError('');
    try {
      const created = await api.preApprove(societyId, {
        name: preApproveForm.name, phone: preApproveForm.phone || undefined,
        purpose: preApproveForm.purpose || undefined, vehicleNumber: preApproveForm.vehicleNumber || undefined,
        appointmentTime: preApproveForm.appointmentTime ? new Date(preApproveForm.appointmentTime).toISOString() : undefined,
        photoBase64: preApprovePhotoBase64 || undefined,
        recurring: preApproveForm.recurring || undefined,
        recurrenceEndDate: preApproveForm.recurring && preApproveForm.recurrenceEndDate ? preApproveForm.recurrenceEndDate : undefined,
      });
      refetch();
      refetchStatusCounts();
      setPreApproveResult(created);
    } catch (err) {
      setPreApproveError((err as ApiError).message || 'Failed to pre-approve visitor');
    } finally { setPreApproveSaving(false); }
  };

  const closePreApprove = () => {
    setShowPreApprove(false); setPreApproveForm(INIT); setPreApproveError(''); setPreApproveResult(null);
    setPreApprovePhotoBase64(''); setPreApprovePhotoPreview('');
  };

  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !checkInCode.trim()) return;
    setCheckInSaving(true); setCheckInError('');
    try {
      const isNumericOtp = /^\d{4,8}$/.test(checkInCode.trim());
      await api.checkIn(societyId, isNumericOtp
        ? { otpCode: checkInCode.trim() }
        : { qrToken: checkInCode.trim() });
      refetch();
      refetchStatusCounts();
      setShowCheckIn(false); setCheckInCode('');
    } catch (err) {
      setCheckInError((err as ApiError).message || 'Check-in failed — invalid or already-used code');
    } finally { setCheckInSaving(false); }
  };

  const btnStyle = (bg: string, color: string, border: string) => ({
    fontSize: 10, padding: '3px 8px', background: bg, color, border: `1px solid ${border}`,
    borderRadius: 6, cursor: 'pointer',
  });
  const iconBtnStyle = (bg: string, color: string, border: string) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, background: bg, color, border: `1px solid ${border}`,
    borderRadius: 6, cursor: 'pointer', flexShrink: 0,
  });

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Visitors
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {totalElements} {statusFilter !== 'all' ? statusFilter : 'total'} entries
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <BulkImportExport onExport={handleExport} exporting={exporting} />
          {isGateStaff && (
            <button onClick={() => setShowCheckIn(true)} className="nx-btn nx-btn-outline" style={{ gap: 6 }}>
              <ScanLine size={14} /> Check In
            </button>
          )}
          <button onClick={() => setShowPreApprove(true)} className="nx-btn nx-btn-outline" style={{ gap: 6 }}>
            <QrCode size={14} /> Pre-approve Visitor
          </button>
          <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Log Visitor
          </button>
        </div>
      </div>

      {actionError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#EF4444' }}>
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          {actionError}
          <button onClick={() => setActionError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      <StatusFilterBar
        options={STATUS_FILTERS.map(f => ({ key: f, label: f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1) }))}
        active={statusFilter}
        onChange={setStatusFilter}
        counts={statusCounts}
        countsLoading={statusCountsLoading}
      />

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          columns={[
            {
              key: 'photo', header: '',
              render: (v: Visitor) => v.photoBase64
                ? <img src={`data:image/jpeg;base64,${v.photoBase64}`} alt={v.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 6 }} />
                : null,
            },
            {
              key: 'name', header: 'Name',
              cellStyle: { fontWeight: 500 },
              sortable: true, sortKey: 'name',
              render: (v: Visitor) => v.name,
            },
            {
              key: 'purpose', header: 'Purpose',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              sortable: true, sortKey: 'purpose',
              render: (v: Visitor) => v.purpose || '—',
            },
            {
              key: 'phone', header: 'Phone',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              sortable: true, sortKey: 'phone',
              render: (v: Visitor) => v.phone || '—',
            },
            {
              key: 'unit', header: 'Unit',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (v: Visitor) => v.unitNumber
                ? (v.blockName ? `${v.blockName} — ${v.unitNumber}` : v.unitNumber)
                : '—',
            },
            {
              key: 'meeting', header: 'Meeting',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (v: Visitor) => v.residentName || '—',
            },
            {
              key: 'vehicle', header: 'Vehicle',
              cellStyle: { color: 'var(--nx-text-muted)' },
              sortable: true, sortKey: 'vehicleNumber',
              render: (v: Visitor) => v.vehicleNumber || '—',
            },
            {
              key: 'entry', header: 'Entry Time',
              cellStyle: { color: 'var(--nx-text-muted)' },
              sortable: true, sortKey: 'entryTime',
              render: (v: Visitor) => v.entryTime ? fmt(v.entryTime) : <span style={{ fontStyle: 'italic' }}>Not arrived</span>,
            },
            {
              key: 'appointment', header: 'Appointment',
              cellStyle: { color: 'var(--nx-text-muted)' },
              sortable: true, sortKey: 'appointmentTime',
              render: (v: Visitor) => v.appointmentTime
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={11} />{fmt(v.appointmentTime)}</span>
                : <span style={{ color: 'var(--nx-text-muted)' }}>—</span>,
            },
            {
              key: 'status', header: 'Status',
              sortable: true, sortKey: 'status',
              render: (v: Visitor) => {
                const sc = statusColor(v.status);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {v.status === 'PRE_APPROVED' ? 'Pre-approved' : v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                  </span>
                );
              },
            },
            {
              key: 'actions', header: 'Actions',
              render: (v: Visitor) => {
                // Only lock approved visits — a pending request that was never acted on
                // shouldn't get stuck unable to be approved/denied/edited/rescheduled.
                const locked = v.status === 'approved' && isPastAppointment(v);
                return (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' as const }}>

                    {/* Status change buttons — Approve/Exit lock once the appointment time has
                        arrived, but Deny stays available so an approved visitor can still be
                        revoked after the fact if someone changes their mind. */}
                    {locked ? (
                      <>
                        <button onClick={() => doAction(v.id, 'deny')} disabled={actionLoading === v.id + 'deny'} className="nx-btn nx-btn-sm" style={btnStyle('rgba(239,68,68,0.1)', '#DC2626', 'rgba(239,68,68,0.2)')}>Deny</button>
                        <span title="Appointment time has arrived — Mark Exit locked" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--nx-text-muted)', padding: '3px 8px', background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.15)', borderRadius: 6 }}>
                          <Lock size={10} /> Locked
                        </span>
                      </>
                    ) : (
                      <>
                        {v.status === 'pending' && (
                          <>
                            <button onClick={() => doAction(v.id, 'approve')} disabled={actionLoading === v.id + 'approve'} className="nx-btn nx-btn-sm" style={btnStyle('rgba(16,185,129,0.1)', '#10B981', 'rgba(16,185,129,0.2)')}>Approve</button>
                            <button onClick={() => doAction(v.id, 'deny')} disabled={actionLoading === v.id + 'deny'} className="nx-btn nx-btn-sm" style={btnStyle('rgba(239,68,68,0.1)', '#DC2626', 'rgba(239,68,68,0.2)')}>Deny</button>
                          </>
                        )}
                        {v.status === 'approved' && (
                          <>
                            <button onClick={() => doAction(v.id, 'exit')} disabled={actionLoading === v.id + 'exit'} className="nx-btn nx-btn-sm" style={btnStyle('rgba(148,163,184,0.1)', '#64748B', 'rgba(148,163,184,0.2)')}>Mark Exit</button>
                            <button onClick={() => doAction(v.id, 'deny')} disabled={actionLoading === v.id + 'deny'} className="nx-btn nx-btn-sm" style={btnStyle('rgba(239,68,68,0.1)', '#DC2626', 'rgba(239,68,68,0.2)')}>Deny</button>
                          </>
                        )}
                        {/* denied and exited: no further transitions allowed */}
                      </>
                    )}

                    {/* Edit — disabled when exited or appointment has arrived */}
                    {v.status === 'exited' || locked ? (
                      <span title={v.status === 'exited' ? 'Cannot edit an exited visitor' : 'Cannot edit after appointment time'} style={iconBtnStyle('rgba(148,163,184,0.05)', 'var(--nx-text-muted)', 'rgba(148,163,184,0.1)')}>
                        <Pencil size={11} />
                      </span>
                    ) : (
                      <button onClick={() => openEdit(v)} title="Edit visitor" style={iconBtnStyle('rgba(99,102,241,0.1)', '#6366F1', 'rgba(99,102,241,0.2)')}>
                        <Pencil size={11} />
                      </button>
                    )}

                    {/* Delete — inline confirm (soft delete) */}
                    {deleteConfirm === v.id ? (
                      <>
                        <button onClick={() => handleDelete(v.id)} disabled={deleteLoading} style={btnStyle('rgba(239,68,68,0.12)', '#DC2626', 'rgba(239,68,68,0.25)')}>Yes</button>
                        <button onClick={() => setDeleteConfirm(null)} style={btnStyle('rgba(148,163,184,0.1)', '#64748B', 'rgba(148,163,184,0.2)')}>No</button>
                      </>
                    ) : (
                      <button onClick={() => setDeleteConfirm(v.id)} title="Delete visitor" style={iconBtnStyle('rgba(239,68,68,0.08)', '#EF4444', 'rgba(239,68,68,0.15)')}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={rows}
          rowKey={v => v.id}
          loading={loading}
          emptyIcon={<DoorOpen size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No visitor entries yet."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search visitors…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* Create modal */}
      {showForm && (
        <Modal
          title="Log Visitor Entry" submitLabel="Log Visitor" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFormError(''); setPhotoBase64(''); setPhotoPreview(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Visitor Name *</label>
            <input name="name" value={form.name} onChange={change} required placeholder="Ravi Kumar" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Phone</label>
              <input name="phone" value={form.phone} onChange={change} placeholder="+91 9876543210" style={inp} />
            </div>
            <div>
              <label style={lbl}>Vehicle Number</label>
              <input name="vehicleNumber" value={form.vehicleNumber} onChange={change} placeholder="KA01AB1234" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Purpose of Visit</label>
            <input name="purpose" value={form.purpose} onChange={change} placeholder="Delivery, Guest, Plumber…" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Flat / Unit</label>
              <SearchSelect
                value={form.unitId}
                onChange={v => setForm(p => ({ ...p, unitId: v, residentId: '' }))}
                options={unitOptions.map(u => ({ value: u.id, label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber }))}
                placeholder={unitOptions.length === 0 ? 'No units available' : 'Select flat…'}
                emptyText="No units found"
                disabled={unitOptions.length === 0}
              />
            </div>
            <div>
              <label style={lbl}>Whom to Meet</label>
              <SearchSelect
                value={form.residentId}
                onChange={v => setForm(p => ({ ...p, residentId: v }))}
                options={unitResidentOptions(form.unitId).map(r => ({ value: r.id, label: `${r.fullName}${r.unitNumber ? ` — ${r.unitNumber}` : ''}` }))}
                placeholder={unitResidentOptions(form.unitId).length === 0 ? 'No residents available' : 'Select resident…'}
                emptyText="No residents found"
                disabled={unitResidentOptions(form.unitId).length === 0}
              />
            </div>
          </div>
          <div>
            <label style={lbl}>Appointment Time (optional)</label>
            <input name="appointmentTime" type="datetime-local" value={form.appointmentTime} onChange={change} style={inp} />
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4, display: 'block' }}>Schedule a specific time slot for this visitor</span>
          </div>
          <div>
            <label style={lbl}>Photo (optional)</label>
            <input type="file" accept="image/*" capture="environment" onChange={e => handlePhotoChange(e, 'create')} style={inp} />
            {photoPreview && (
              <img src={photoPreview} alt="Visitor preview" style={{ marginTop: 8, width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--nx-border)' }} />
            )}
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {showEdit && (
        <Modal
          title="Edit Visitor" submitLabel="Save Changes" error={editError} isLoading={editSaving}
          onClose={() => { setShowEdit(false); setEditId(null); setEditError(''); }}
          onSubmit={handleEdit}
        >
          <div>
            <label style={lbl}>Visitor Name *</label>
            <input name="name" value={editForm.name} onChange={editChange} required placeholder="Ravi Kumar" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Phone</label>
              <input name="phone" value={editForm.phone} onChange={editChange} placeholder="+91 9876543210" style={inp} />
            </div>
            <div>
              <label style={lbl}>Vehicle Number</label>
              <input name="vehicleNumber" value={editForm.vehicleNumber} onChange={editChange} placeholder="KA01AB1234" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Purpose of Visit</label>
            <input name="purpose" value={editForm.purpose} onChange={editChange} placeholder="Delivery, Guest, Plumber…" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Flat / Unit</label>
              <SearchSelect
                value={editForm.unitId}
                onChange={v => setEditForm(p => ({ ...p, unitId: v, residentId: '' }))}
                options={unitOptions.map(u => ({ value: u.id, label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber }))}
                placeholder={unitOptions.length === 0 ? 'No units available' : 'Select flat…'}
                emptyText="No units found"
                disabled={unitOptions.length === 0}
              />
            </div>
            <div>
              <label style={lbl}>Whom to Meet</label>
              <SearchSelect
                value={editForm.residentId}
                onChange={v => setEditForm(p => ({ ...p, residentId: v }))}
                options={unitResidentOptions(editForm.unitId).map(r => ({ value: r.id, label: `${r.fullName}${r.unitNumber ? ` — ${r.unitNumber}` : ''}` }))}
                placeholder={unitResidentOptions(editForm.unitId).length === 0 ? 'No residents available' : 'Select resident…'}
                emptyText="No residents found"
                disabled={unitResidentOptions(editForm.unitId).length === 0}
              />
            </div>
          </div>
          <div>
            <label style={lbl}>Appointment Time</label>
            <input name="appointmentTime" type="datetime-local" value={editForm.appointmentTime} onChange={editChange} style={inp} />
          </div>
        </Modal>
      )}
      {/* Pre-approve modal */}
      {showPreApprove && (
        preApproveResult ? (
          <Modal
            title="Visitor Pass Generated" submitLabel="Done" isLoading={false}
            onClose={closePreApprove}
            onSubmit={(e) => { e.preventDefault(); closePreApprove(); }}
          >
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', textAlign: 'center' }}>
              Share the QR code or OTP below with {preApproveResult.name} — either can be used to check in at the gate.
              {preApproveResult.phone && ' It has also been sent to their phone via WhatsApp/SMS.'}
              {preApproveResult.recurring && ' This pass is recurring — the same OTP works for every visit'}
              {preApproveResult.recurring && preApproveResult.recurrenceEndDate && ` through ${preApproveResult.recurrenceEndDate}`}
              {preApproveResult.recurring && '.'}
            </p>
            <QrPreview value={preApproveResult.qrToken || ''} />
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>OTP</div>
              <div style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--nx-text-primary)' }}>
                {preApproveResult.otpCode}
              </div>
            </div>
          </Modal>
        ) : (
          <Modal
            title="Pre-approve Visitor" submitLabel="Generate Pass" error={preApproveError} isLoading={preApproveSaving}
            onClose={closePreApprove}
            onSubmit={handlePreApprove}
          >
            <div>
              <label style={lbl}>Visitor Name *</label>
              <input
                value={preApproveForm.name}
                onChange={e => setPreApproveForm(p => ({ ...p, name: e.target.value }))}
                required placeholder="Ravi Kumar" style={inp} autoFocus
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>Phone</label>
                <input
                  value={preApproveForm.phone}
                  onChange={e => setPreApproveForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91 9876543210" style={inp}
                />
              </div>
              <div>
                <label style={lbl}>Vehicle Number</label>
                <input
                  value={preApproveForm.vehicleNumber}
                  onChange={e => setPreApproveForm(p => ({ ...p, vehicleNumber: e.target.value }))}
                  placeholder="KA01AB1234" style={inp}
                />
              </div>
            </div>
            <div>
              <label style={lbl}>Purpose of Visit</label>
              <input
                value={preApproveForm.purpose}
                onChange={e => setPreApproveForm(p => ({ ...p, purpose: e.target.value }))}
                placeholder="Guest, Cab, Plumber…" style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Expected Arrival (optional)</label>
              <input
                type="datetime-local"
                value={preApproveForm.appointmentTime}
                onChange={e => setPreApproveForm(p => ({ ...p, appointmentTime: e.target.value }))}
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Photo (optional)</label>
              <input type="file" accept="image/*" capture="environment" onChange={e => handlePhotoChange(e, 'preApprove')} style={inp} />
              {preApprovePhotoPreview && (
                <img src={preApprovePhotoPreview} alt="Visitor preview" style={{ marginTop: 8, width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--nx-border)' }} />
              )}
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--nx-text-primary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={preApproveForm.recurring}
                  onChange={e => setPreApproveForm(p => ({ ...p, recurring: e.target.checked }))}
                />
                Recurring guest (same OTP works for every visit, e.g. daily maid/driver)
              </label>
            </div>
            {preApproveForm.recurring && (
              <div>
                <label style={lbl}>Valid Until (optional)</label>
                <input
                  type="date"
                  value={preApproveForm.recurrenceEndDate}
                  onChange={e => setPreApproveForm(p => ({ ...p, recurrenceEndDate: e.target.value }))}
                  style={inp}
                />
              </div>
            )}
          </Modal>
        )
      )}

      {/* Gate check-in modal */}
      {showCheckIn && (
        <Modal
          title="Gate Check-in" submitLabel="Check In" error={checkInError} isLoading={checkInSaving}
          onClose={() => { setShowCheckIn(false); setCheckInCode(''); setCheckInError(''); }}
          onSubmit={handleCheckIn}
        >
          <div>
            <label style={lbl}>QR Token or OTP *</label>
            <input
              value={checkInCode}
              onChange={e => setCheckInCode(e.target.value)}
              required placeholder="Scan QR or enter the 6-digit OTP" style={inp} autoFocus
            />
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4, display: 'block' }}>
              A numeric code is treated as an OTP; anything else is treated as a QR token.
            </span>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default VisitorsPage;
